import type { GameState, RailRoute } from '../state';
import { D, mul, sub, gte, type Decimal } from '../math/num';
import { chance, nextInt } from '../math/rng';
import {
  depotsAdjacent,
  RAIL_PER_DEPOT,
  RAIL_PER_LOOP,
  RAIL_CAP,
  RAIL_COST_MULT,
  DERAIL_CHECK_MS,
  DERAIL_CHANCE,
  DERAIL_REPAIR_MS,
} from '../data/rails';
import { waveScrap } from './spawn';
import { BOSS_EVERY } from './spawn';

/**
 * Supply Lines sim (data/rails.ts holds the numbers). Routes live in state;
 * derailments roll off the seeded RNG once per check period, so online and
 * offline replay agree.
 */

export interface RailsOpResult {
  ok: boolean;
  reason?: string;
}

/** Depots = cleared 10-wave segments (their boss has fallen). */
export function clearedDepots(state: GameState): number {
  return Math.floor((state.combat.highestWave - 1) / BOSS_EVERY);
}

export function routeKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

export function hasRoute(state: GameState, a: number, b: number): boolean {
  const k = routeKey(a, b);
  return state.rails.routes.some((r) => routeKey(r.a, r.b) === k);
}

/** Track between two depots costs Scrap scaled to the farther depot's boss wave. */
export function routeCost(a: number, b: number): Decimal {
  return mul(waveScrap(Math.max(a, b) * BOSS_EVERY), D(RAIL_COST_MULT));
}

export function layRoute(state: GameState, a: number, b: number): RailsOpResult {
  const r = state.rails;
  if (!r.unlocked) return { ok: false, reason: 'Supply Lines are not online.' };
  if (!depotsAdjacent(a, b)) return { ok: false, reason: 'Depots must be adjacent on the map.' };
  const depots = clearedDepots(state);
  if (a > depots || b > depots) return { ok: false, reason: 'Clear that segment’s boss first.' };
  if (hasRoute(state, a, b)) return { ok: false, reason: 'Track already laid.' };
  const cost = routeCost(a, b);
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  r.routes.push({ a: Math.min(a, b), b: Math.max(a, b), derailedUntilMs: 0 });
  r.built += 1;
  return { ok: true };
}

export function isDerailed(state: GameState, route: RailRoute): boolean {
  return route.derailedUntilMs > state.elapsedMs;
}

/** One click re-rails instantly (the click IS the repair). */
export function repairRoute(state: GameState, a: number, b: number): RailsOpResult {
  const k = routeKey(a, b);
  const route = state.rails.routes.find((x) => routeKey(x.a, x.b) === k);
  if (!route) return { ok: false, reason: 'No such track.' };
  if (!isDerailed(state, route)) return { ok: false, reason: 'That track is fine.' };
  route.derailedUntilMs = 0;
  return { ok: true };
}

/** Once per check period a route may derail (auto-re-rails after a while). */
export function stepRails(state: GameState, _dtMs: number): void {
  const r = state.rails;
  if (!r.unlocked || r.routes.length === 0) return;
  if (state.elapsedMs - r.lastDerailMs < DERAIL_CHECK_MS) return;
  r.lastDerailMs = state.elapsedMs;
  const healthy = r.routes.filter((x) => !isDerailed(state, x));
  if (healthy.length === 0) return;
  if (!chance(state.rng, DERAIL_CHANCE)) return;
  const victim = healthy[nextInt(state.rng, 0, healthy.length - 1)]!;
  victim.derailedUntilMs = state.elapsedMs + DERAIL_REPAIR_MS;
}

export interface RailNetwork {
  /** Depots touched by at least one healthy route. */
  connected: number;
  /** Independent cycles in the healthy network (loops pay extra). */
  loops: number;
  /** Scrap bonus fraction, capped. */
  bonus: number;
}

/** Connected depots + cycle rank (edges − nodes + components) of the healthy net. */
export function railNetwork(state: GameState): RailNetwork {
  const healthy = state.rails.routes.filter((x) => !isDerailed(state, x));
  const nodes = new Set<number>();
  for (const e of healthy) { nodes.add(e.a); nodes.add(e.b); }

  // union-find for component count
  const parent = new Map<number, number>();
  const find = (x: number): number => {
    let p = parent.get(x) ?? x;
    if (p !== x) { p = find(p); parent.set(x, p); }
    return p;
  };
  for (const n of nodes) parent.set(n, n);
  for (const e of healthy) parent.set(find(e.a), find(e.b));
  const components = new Set([...nodes].map((n) => find(n))).size;

  const loops = nodes.size === 0 ? 0 : healthy.length - nodes.size + components;
  const bonus = Math.min(RAIL_CAP, RAIL_PER_DEPOT * nodes.size + RAIL_PER_LOOP * loops);
  return { connected: nodes.size, loops, bonus };
}

/** The Scrap multiplier the network feeds into combat income. */
export function railScrapBonus(state: GameState): Decimal {
  if (!state.rails.unlocked) return D(1);
  return D(1 + railNetwork(state).bonus);
}

/** Lay the first spike — rides the Sir Pent kill. */
export function unlockRails(state: GameState): void {
  if (state.rails.unlocked) return;
  state.rails.unlocked = true;
  state.meta.unlocks.rails = true;
}
